"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

var _react = _interopRequireWildcard(require("react"));

var _propTypes = _interopRequireDefault(require("prop-types"));

var _moment = _interopRequireDefault(require("react-day-picker/moment"));

var _reactDayPicker = _interopRequireDefault(require("react-day-picker"));

var _isEqual = _interopRequireDefault(require("lodash/isEqual"));

var _popover = _interopRequireDefault(require("../../__internal__/popover"));

var _date = _interopRequireDefault(require("../../utils/helpers/date/date"));

var _useLocale = _interopRequireDefault(require("../../hooks/__internal__/useLocale"));

var _navbar = _interopRequireDefault(require("./navbar"));

var _weekday = _interopRequireDefault(require("./weekday"));

var _dayPicker = _interopRequireDefault(require("./day-picker.style"));

function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }

function _getRequireWildcardCache() { if (typeof WeakMap !== "function") return null; var cache = new WeakMap(); _getRequireWildcardCache = function () { return cache; }; return cache; }

function _interopRequireWildcard(obj) { if (obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const DatePicker = ({
  inputElement,
  inputDate,
  handleDateSelect,
  minDate,
  maxDate,
  selectedDate,
  disablePortal
}) => {
  const l = (0, _useLocale.default)();
  const locale = l.locale();
  const formats = l.date.formats.inputs();
  const format = l.date.formats.javascript();
  const [lastValidDate, setLastValidDate] = (0, _react.useState)(_date.default.formatDateString(new Date().toString()));
  const [localeData, setLocaleData] = (0, _react.useState)({
    locale,
    formats,
    format
  });
  const ref = (0, _react.useRef)(null);
  const popoverModifiers = (0, _react.useMemo)(() => [{
    name: "offset",
    options: {
      offset: [0, 3]
    }
  }, {
    name: "preventOverflow",
    options: {
      mainAxis: false
    }
  }], []);
  (0, _react.useEffect)(() => {
    if (localeData.format === format && (0, _isEqual.default)(localeData.formats, formats) && localeData.locale === locale) {
      return;
    }

    setLocaleData({
      locale,
      formats,
      format
    });
  }, [localeData.format, localeData.formats, localeData.locale, format, formats, locale]);
  (0, _react.useEffect)(() => {
    let monthDate;

    const isoFormattedInputDate = _date.default.formatValue({
      value: inputDate,
      ...localeData
    });

    if (isDateValid(isoFormattedInputDate)) {
      monthDate = new Date(isoFormattedInputDate);
      setLastValidDate(isoFormattedInputDate);
    } else {
      monthDate = new Date(lastValidDate);
    }

    ref.current.showMonth(monthDate);
  }, [inputDate, lastValidDate, localeData]);

  const handleDayClick = (date, modifiers) => {
    if (!modifiers.disabled) {
      handleDateSelect(date);
    }
  };

  const datePickerProps = {
    disabledDays: getDisabledDays(minDate, maxDate, localeData),
    enableOutsideDays: true,
    fixedWeeks: true,
    initialMonth: selectedDate || undefined,
    inline: true,
    locale: l.locale(),
    localeUtils: _moment.default,
    navbarElement: /*#__PURE__*/_react.default.createElement(_navbar.default, null),
    onDayClick: handleDayClick,
    selectedDays: selectedDate || undefined,
    weekdayElement: weekdayElementProps => {
      const {
        className,
        weekday,
        localeUtils
      } = weekdayElementProps;
      const weekdayLong = localeUtils.formatWeekdayLong(weekday, l.locale());
      const weekdayShort = localeUtils.formatWeekdayShort(weekday, l.locale());
      return /*#__PURE__*/_react.default.createElement(_weekday.default, {
        className: className,
        title: weekdayLong
      }, weekdayShort);
    }
  };
  return /*#__PURE__*/_react.default.createElement(_popover.default, {
    placement: "bottom-start",
    reference: inputElement,
    modifiers: popoverModifiers,
    disablePortal: disablePortal
  }, /*#__PURE__*/_react.default.createElement(_dayPicker.default, null, /*#__PURE__*/_react.default.createElement(_reactDayPicker.default, _extends({}, datePickerProps, {
    ref: ref
  }))));
};

DatePicker.propTypes = {
  /** Minimum possible date */
  minDate: _propTypes.default.string,

  /** Maximum possible date */
  maxDate: _propTypes.default.string,

  /** Boolean to toggle where DatePicker is rendered in relation to the Date Input */
  disablePortal: _propTypes.default.bool,

  /* The string value in the date input */
  inputDate: _propTypes.default.string,

  /** Element that the DatePicker will be displayed under */
  inputElement: _propTypes.default.object.isRequired,

  /** Currently selected date */
  selectedDate: _propTypes.default.oneOfType([_propTypes.default.object, _propTypes.default.string]),

  /** Callback to set selected date */
  handleDateSelect: _propTypes.default.func
};
/**
 * Checks if date can be transformed to native js Date object
 */

function isDateValid(string) {
  const date = new Date(string);
  return date.toString() !== "Invalid Date";
}
/**
 * Returns the disabled array of days specified by props maxDate and minDate
 */


function getDisabledDays(minDate, maxDate, {
  locale,
  formats,
  format
}) {
  const days = [];

  if (!minDate && !maxDate) {
    return null;
  }

  if (minDate && checkIsoFormatAndLength(minDate, locale, formats, format)) {
    days.push({
      before: _date.default.stringToDate(minDate)
    });
  }

  if (maxDate && checkIsoFormatAndLength(maxDate, locale, formats, format)) {
    days.push({
      after: _date.default.stringToDate(maxDate)
    });
  }

  return days;
}

function checkIsoFormatAndLength(date, locale, formats, format) {
  if (date.length !== 10 || !_date.default.isValidDate({
    value: date,
    options: {
      defaultValue: "YYYY-MM-DD"
    },
    locale,
    formats,
    format
  })) {
    return false;
  }

  const array = date.split("-");
  return array.length === 3 && array[0].length === 4 && array[1].length === 2 && array[2].length === 2;
}

var _default = DatePicker;
exports.default = _default;